import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";
import { useCurrency } from "./use-currency";

/**
 * Renders the authoritative GBP amount and, when the shopper has selected a
 * different display currency, a secondary "≈ converted" value. GBP is always
 * the primary, source-of-truth figure — bidding/checkout stay GBP-only.
 */
export function Price({
  pence,
  className,
  approxClassName,
  size = "md",
}: {
  pence: number | null | undefined;
  className?: string;
  approxClassName?: string;
  size?: "sm" | "md";
}) {
  const { t } = useTranslation("currency");
  const { isGBP, formatGBP, formatConverted, currency } = useCurrency();
  const amount = pence ?? 0;

  return (
    <span className={cn("inline-flex flex-wrap items-baseline gap-x-1.5", className)}>
      <span>{formatGBP(amount)}</span>
      {!isGBP && (
        <span
          className={cn(
            size === "sm" ? "text-xs" : "text-sm",
            "text-muted-foreground",
            approxClassName,
          )}
        >
          · {t("approx", "≈ {value}", { value: formatConverted(amount, currency) })}
        </span>
      )}
    </span>
  );
}

export function CurrencyDisclaimer({ className }: { className?: string }) {
  const { t } = useTranslation("currency");
  return (
    <p className={cn("text-xs text-muted-foreground", className)}>
      {t(
        "disclaimer",
        "Converted prices are estimates. All bids and payments are in GBP.",
      )}
    </p>
  );
}
